import { DEFAULT_TYPESETTING } from 'modules/contestacao/components/document/typesetting/typesetting.const';
import { ReviewRejectReason } from 'modules/contestacao/pages/review/contestacao-review.model';
import {
	ContestacaoTemplateConfig,
	ContestacaoTemplateLegalRequestEntry,
	ContestacaoTemplateThesisEntry,
	ContestacaoTemplateTopicEntry,
} from 'services/contestacao-template/contestacao-template.model';
import { Fact, FactSchema } from 'services/fact/fact.model';
import { Tag } from 'services/tag/tag.model';
import { AuditUser } from 'services/user/user.model';
import {
	MOCK_AUDIT_USER,
	MOCK_CONTEXT_CLIENT,
	MOCK_PEOPLE,
	MOCK_VERTICAL_INFO,
} from '../shared/mock-context.const';
import {
	MOCK_FACT_ROSTER,
	MOCK_LEGAL_REQUEST_ROSTER,
	MOCK_TAG_ROSTER,
	MOCK_THESIS_ROSTER,
	MOCK_TOPIC_ROSTER,
} from '../shared/mock-knowledge-roster.const';

/**
 * Dados da Contestação (revisão + templates), montados SOMENTE a partir do roster da base
 * de conhecimento — mesmos ids/nomes que mocks/knowledge-base usa.
 */

export type ThesisRosterId = (typeof MOCK_THESIS_ROSTER)[number]['id'];
export type FactSlug = (typeof MOCK_FACT_ROSTER)[number]['slug'];

export const KB_CREATED_AT = '2026-01-15T13:00:00.000Z';
export const KB_UPDATED_AT = '2026-07-02T17:30:00.000Z';

export function daysAgoIso(days: number, hour = 10, minute = 0): string {
	const date = new Date();
	date.setDate(date.getDate() - days);
	date.setHours(hour, minute, 0, 0);
	return date.toISOString();
}

export function slugify(text: string): string {
	return text
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/(^-|-$)/g, '');
}

// ── Tags ─────────────────────────────────────────────────────────────────────

export const CT_TAGS: Record<string, Tag> = Object.fromEntries(
	MOCK_TAG_ROSTER.map((tag) => [
		tag.id,
		{
			id: tag.id,
			name: tag.name,
			slug: slugify(tag.name),
			createdAt: KB_CREATED_AT,
			updatedAt: KB_UPDATED_AT,
			createdBy: MOCK_AUDIT_USER,
			updatedBy: MOCK_AUDIT_USER,
		} satisfies Tag,
	]),
);

// ── Fatos ────────────────────────────────────────────────────────────────────

const FACT_DETAILS: Record<
	FactSlug,
	{ description: string; schema: FactSchema; examples: string[]; tagIds: string[] }
> = {
	'data-admissao': {
		description: 'Data de início do contrato de trabalho informada na petição inicial.',
		schema: { type: 'string', format: 'date' },
		examples: ['2019-03-11'],
		tagIds: ['tag-004'],
	},
	'data-demissao': {
		description: 'Data de término do contrato de trabalho informada na petição inicial.',
		schema: { type: 'string', format: 'date' },
		examples: ['2025-11-28'],
		tagIds: ['tag-002', 'tag-004'],
	},
	'jornada-alegada': {
		description: 'Jornada de trabalho que o reclamante alega ter cumprido.',
		schema: { type: 'string' },
		examples: ['Segunda a sábado, das 7h às 19h, com 30 minutos de intervalo'],
		tagIds: ['tag-001'],
	},
	'possui-cartao-ponto': {
		description: 'Indica se a empresa juntou os cartões de ponto do período contratual.',
		schema: { type: 'boolean' },
		examples: ['true'],
		tagIds: ['tag-001'],
	},
	'motivo-rescisao': {
		description: 'Modalidade de desligamento informada pelo reclamante.',
		schema: {
			type: 'string',
			enum: ['Dispensa sem justa causa', 'Pedido de demissão', 'Rescisão indireta', 'Justa causa'],
		},
		examples: ['Dispensa sem justa causa'],
		tagIds: ['tag-002'],
	},
	'trct-assinado': {
		description: 'Indica se o TRCT foi assinado pelo empregado sem ressalvas.',
		schema: { type: 'boolean' },
		examples: ['true'],
		tagIds: ['tag-002'],
	},
	'foto-local-trabalho': {
		description: 'Fotografia do posto de trabalho juntada pela empresa.',
		schema: { type: 'string', format: 'uri' },
		examples: [],
		tagIds: ['tag-005'],
	},
};

export const CT_FACTS: Record<string, Fact> = Object.fromEntries(
	MOCK_FACT_ROSTER.map((fact) => {
		const details = FACT_DETAILS[fact.slug];
		const entity: Fact = {
			id: fact.id,
			slug: fact.slug,
			name: fact.name,
			classification: fact.classification,
			description: details.description,
			schema: details.schema,
			examples: details.examples,
			tags: details.tagIds.map((tagId) => CT_TAGS[tagId]),
			createdAt: KB_CREATED_AT,
			updatedAt: KB_UPDATED_AT,
			createdBy: MOCK_AUDIT_USER,
			updatedBy: MOCK_AUDIT_USER,
			verticalId: MOCK_VERTICAL_INFO['trabalhista'].id,
		};
		return [fact.id, entity];
	}),
);

export function factBySlug(slug: string): Fact | undefined {
	return Object.values(CT_FACTS).find((fact) => fact.slug === slug);
}

// ── Pedidos ──────────────────────────────────────────────────────────────────

const LEGAL_REQUEST_DETAILS: Record<string, { description: string; tagIds: string[] }> = {
	'lr-001': {
		description:
			'Pagamento de horas extras excedentes da 8ª diária e 44ª semanal, com reflexos em DSR, férias + 1/3, 13º salário e FGTS.',
		tagIds: ['tag-001'],
	},
	'lr-002': {
		description:
			'Pagamento do adicional noturno de 20% sobre as horas laboradas entre 22h e 5h, com reflexos.',
		tagIds: ['tag-001'],
	},
	'lr-003': {
		description:
			'Multa equivalente a um salário pelo atraso no pagamento das verbas rescisórias (art. 477, § 8º, da CLT).',
		tagIds: ['tag-002'],
	},
	'lr-004': {
		description:
			'Indenização por danos morais decorrentes de conduta do empregador durante o contrato.',
		tagIds: ['tag-003'],
	},
	'lr-005': {
		description:
			'Condenação ao pagamento de honorários advocatícios sucumbenciais (art. 791-A da CLT).',
		tagIds: [],
	},
};

export function legalRequestEntry(
	legalRequestId: string,
	order: number,
): ContestacaoTemplateLegalRequestEntry {
	const roster = MOCK_LEGAL_REQUEST_ROSTER.find((item) => item.id === legalRequestId);
	const details = LEGAL_REQUEST_DETAILS[legalRequestId];
	return {
		id: legalRequestId,
		name: roster?.name ?? 'Pedido',
		slug: slugify(roster?.name ?? legalRequestId),
		description: details?.description ?? '',
		productSetupId: null,
		isGlobal: true,
		tags: (details?.tagIds ?? []).map((tagId) => CT_TAGS[tagId]),
		status: 'ACTIVE',
		createdAt: KB_CREATED_AT,
		createdBy: MOCK_AUDIT_USER,
		updatedAt: KB_UPDATED_AT,
		updatedBy: MOCK_AUDIT_USER,
		deletedAt: null,
		deletedBy: null,
		order,
	};
}

// ── Teses ────────────────────────────────────────────────────────────────────

interface ThesisText {
	description: string;
	/** HTML com variáveis `{{slug-do-fato}}` */
	content: string;
	factSlugs: FactSlug[];
}

export const THESIS_TEXTS: Record<ThesisRosterId, ThesisText> = {
	'thesis-001': {
		description:
			'Pedidos sem liquidação ou causa de pedir delimitada violam o art. 840, § 1º, da CLT.',
		factSlugs: [],
		content:
			'<p>Preliminarmente, requer a Reclamada seja declarada a inépcia da petição inicial quanto aos pedidos formulados de maneira genérica, sem a indicação de valores e da causa de pedir correspondente, em afronta ao art. 840, § 1º, da CLT, com a redação dada pela Lei nº 13.467/2017.</p>' +
			'<p>A ausência de liquidação dos pedidos e de delimitação dos fatos impede o pleno exercício do contraditório e da ampla defesa (art. 5º, LV, da Constituição Federal), razão pela qual devem ser extintos sem resolução do mérito, nos termos do art. 330, § 1º, do CPC, aplicado subsidiariamente por força do art. 769 da CLT.</p>',
	},
	'thesis-002': {
		description:
			'Pretensões anteriores aos cinco anos do ajuizamento estão prescritas (art. 7º, XXIX, da CF).',
		factSlugs: ['data-admissao', 'data-demissao'],
		content:
			'<p>A parte Reclamante foi admitida em {{data-admissao}} e teve o contrato de trabalho encerrado em {{data-demissao}}. Considerando a data do ajuizamento da presente reclamação, impõe-se a pronúncia da prescrição quinquenal, nos termos do art. 7º, XXIX, da Constituição Federal e do art. 11 da CLT.</p>' +
			'<p>Requer, assim, sejam declaradas prescritas as pretensões anteriores aos cinco anos que antecedem o ajuizamento da ação, extinguindo-se o processo com resolução do mérito quanto a elas (art. 487, II, do CPC), conforme a Súmula nº 308, I, do TST.</p>',
	},
	'thesis-003': {
		description:
			'Os registros de ponto variáveis e assinados são prova válida da jornada (Súmula 338 do TST).',
		factSlugs: ['jornada-alegada', 'possui-cartao-ponto'],
		content:
			'<p>A parte Reclamante alega ter cumprido jornada de {{jornada-alegada}}, sem a devida contraprestação. Não lhe assiste razão. A Reclamada mantém controle de jornada por registro eletrônico de ponto, em conformidade com o art. 74, § 2º, da CLT e com a Portaria MTP nº 671/2021, cujos espelhos seguem anexos, assinados mensalmente pelo empregado.</p>' +
			'<p>Os cartões registram horários variáveis de entrada e saída, afastando a presunção de invalidade prevista na Súmula nº 338, III, do TST. As horas extras eventualmente prestadas foram quitadas ou compensadas, conforme os recibos de pagamento. Cabe à parte autora o ônus de comprovar o fato constitutivo de seu direito (art. 818, I, da CLT), do qual não se desincumbiu.</p>',
	},
	'thesis-004': {
		description:
			'Horas excedentes foram compensadas em banco de horas válido (art. 59, § 5º, da CLT).',
		factSlugs: ['jornada-alegada'],
		content:
			'<p>Ainda que se admitisse o labor em sobrejornada na forma alegada ({{jornada-alegada}}), a Reclamada adotava regime de compensação por banco de horas, instituído por acordo individual escrito, nos termos do art. 59, § 5º, da CLT, com compensação no período máximo de seis meses.</p>' +
			'<p>As horas excedentes foram lançadas no banco de horas e compensadas com folgas, conforme os extratos anexos, não havendo saldo positivo em favor do empregado na data da rescisão. Improcede, pois, o pedido de horas extras e reflexos.</p>',
	},
	'thesis-005': {
		description: 'Jornada integralmente diurna afasta o adicional noturno (art. 73 da CLT).',
		factSlugs: ['jornada-alegada'],
		content:
			'<p>Não prospera o pedido de adicional noturno. Conforme se extrai dos controles de jornada, a parte Reclamante laborava em horário diurno, com término da jornada antes das 22h, não se enquadrando na hipótese do art. 73, § 2º, da CLT.</p>' +
			'<p>Nos raros dias em que houve labor após as 22h, o adicional de 20% foi regularmente pago sob a rubrica “Adicional Noturno”, com observância da hora noturna reduzida, conforme os holerites juntados. Requer a improcedência do pedido.</p>',
	},
	'thesis-006': {
		description:
			'Verbas quitadas no prazo do art. 477, § 6º, da CLT afastam as multas dos arts. 467 e 477.',
		factSlugs: ['data-demissao'],
		content:
			'<p>As verbas rescisórias foram quitadas dentro do prazo de dez dias contados do término do contrato, ocorrido em {{data-demissao}}, conforme comprovante de transferência bancária anexo, em estrita observância ao art. 477, § 6º, da CLT.</p>' +
			'<p>Inexistindo atraso no pagamento, é indevida a multa prevista no § 8º do art. 477 da CLT, bem como a penalidade do art. 467 da CLT, uma vez que não há verbas rescisórias incontroversas pendentes de quitação.</p>',
	},
	'thesis-007': {
		description:
			'O TRCT assinado sem ressalvas dá quitação às parcelas discriminadas (Súmula 330 do TST).',
		factSlugs: ['trct-assinado', 'motivo-rescisao'],
		content:
			'<p>O Termo de Rescisão do Contrato de Trabalho foi regularmente assinado pela parte Reclamante, sem qualquer ressalva, dando plena quitação às parcelas nele discriminadas, nos termos do art. 477, § 2º, da CLT e da Súmula nº 330 do TST.</p>' +
			'<p>A rescisão se deu na modalidade “{{motivo-rescisao}}”, e todas as verbas devidas em razão dessa forma de desligamento foram corretamente calculadas e pagas, não havendo diferenças a serem deferidas.</p>',
	},
	'thesis-008': {
		description: 'Sem prova de ato ilícito, dano e nexo causal não há dano moral indenizável.',
		factSlugs: ['motivo-rescisao'],
		content:
			'<p>A parte Reclamante pleiteia indenização por danos morais sem apresentar qualquer elemento de prova capaz de demonstrar a ocorrência de ato ilícito, do dano e do nexo causal, requisitos indispensáveis à responsabilização civil (arts. 186 e 927 do Código Civil).</p>' +
			'<p>O dano moral não se presume: cabe ao autor comprovar a efetiva ofensa à sua honra, imagem ou intimidade (art. 818, I, da CLT). Meros dissabores decorrentes da relação de emprego, inclusive do desligamento por “{{motivo-rescisao}}”, não configuram dano extrapatrimonial indenizável. Requer a improcedência do pedido.</p>',
	},
	'thesis-009': {
		description: 'Eventual indenização deve observar os parâmetros do art. 223-G da CLT.',
		factSlugs: [],
		content:
			'<p>Na remota hipótese de condenação, o que se admite apenas por amor ao debate, requer a Reclamada que o valor da indenização observe os parâmetros do art. 223-G da CLT, considerando a natureza da ofensa, a ausência de reiteração e a situação econômica das partes.</p>' +
			'<p>O montante pleiteado mostra-se manifestamente desproporcional, configurando pretensão de enriquecimento sem causa, vedado pelo art. 884 do Código Civil. Pugna, assim, pela fixação de valor módico, em observância aos princípios da razoabilidade e da proporcionalidade.</p>',
	},
	'thesis-010': {
		description:
			'Improcedentes os pedidos, não cabem honorários à parte autora; subsidiariamente, percentual mínimo.',
		factSlugs: [],
		content:
			'<p>Improcedentes os pedidos principais, não há que se falar em condenação da Reclamada ao pagamento de honorários sucumbenciais. Subsidiariamente, requer seja observado o percentual mínimo de 5% previsto no art. 791-A da CLT.</p>' +
			'<p>Requer, ainda, a condenação da parte Reclamante ao pagamento de honorários sucumbenciais em favor dos patronos da Reclamada, sobre o valor dos pedidos julgados improcedentes, observada a decisão do STF na ADI 5766 quanto aos beneficiários da justiça gratuita.</p>',
	},
};

const TRABALHISTA = MOCK_VERTICAL_INFO['trabalhista'];

export function thesisEntry(
	thesisId: string,
	order: number,
	entryPrefix: string,
): ContestacaoTemplateThesisEntry {
	const roster = MOCK_THESIS_ROSTER.find((item) => item.id === thesisId);
	const text = roster ? THESIS_TEXTS[roster.id] : undefined;
	const name = roster?.name ?? 'Tese';
	return {
		id: thesisId,
		name,
		slug: slugify(name),
		description: text?.description ?? '',
		tipoTeseProcessual: roster?.tipo ?? 'MERITO',
		content: text?.content ?? '<p></p>',
		ruleIncludes: {},
		ruleExcludes: null,
		tags: (roster?.tagIds ?? []).map((tagId) => CT_TAGS[tagId]),
		facts: (text?.factSlugs ?? [])
			.map((slug) => factBySlug(slug))
			.filter((fact): fact is Fact => !!fact),
		productSetupId: null,
		verticalId: TRABALHISTA.id,
		vertical: { id: TRABALHISTA.id, name: TRABALHISTA.name, description: 'Direito do Trabalho' },
		legalRequests: (roster?.legalRequestIds ?? []).map((legalRequestId, index) =>
			legalRequestEntry(legalRequestId, index),
		),
		isGlobal: true,
		status: 'ACTIVE',
		createdAt: KB_CREATED_AT,
		createdBy: MOCK_AUDIT_USER,
		updatedAt: KB_UPDATED_AT,
		updatedBy: MOCK_AUDIT_USER,
		deletedAt: null,
		deletedBy: null,
		thesisEntryId: `${entryPrefix}-${thesisId}`,
		order,
	};
}

export function topicEntry(
	topicId: string,
	order: number,
	thesisIds: string[],
	entryPrefix: string,
): ContestacaoTemplateTopicEntry {
	const roster = MOCK_TOPIC_ROSTER.find((item) => item.id === topicId);
	const name = roster?.name ?? 'Tópico';
	return {
		id: topicId,
		name,
		slug: slugify(name),
		order,
		description: TOPIC_DESCRIPTIONS[topicId] ?? null,
		productSetupId: null,
		isGlobal: true,
		enableBatchApproval: roster?.enableBatchApproval ?? false,
		createdAt: KB_CREATED_AT,
		createdBy: MOCK_AUDIT_USER,
		updatedAt: KB_UPDATED_AT,
		updatedBy: MOCK_AUDIT_USER,
		deletedAt: null,
		deletedBy: null,
		topicEntryId: `${entryPrefix}-${topicId}`,
		theses: thesisIds.map((thesisId, index) => thesisEntry(thesisId, index, entryPrefix)),
	};
}

const TOPIC_DESCRIPTIONS: Record<string, string> = {
	'topic-001': 'Questões processuais arguidas antes do mérito.',
	'topic-002': 'Prescrição e decadência.',
	'topic-003': 'Horas extras, intervalos, banco de horas e adicional noturno.',
	'topic-004': 'Verbas rescisórias, TRCT e multas dos arts. 467 e 477 da CLT.',
	'topic-005': 'Indenizações por danos extrapatrimoniais.',
	'topic-006': 'Honorários sucumbenciais e custas processuais.',
};

/** Estrutura completa: todos os tópicos do roster com suas teses, na ordem do roster */
export function fullStructure(): { topicId: string; thesisIds: string[] }[] {
	return MOCK_TOPIC_ROSTER.map((topic) => ({
		topicId: topic.id,
		thesisIds: MOCK_THESIS_ROSTER.filter((thesis) => thesis.topicId === topic.id).map(
			(thesis) => thesis.id,
		),
	}));
}

export function buildTopics(
	structure: { topicId: string; thesisIds: string[] }[],
	entryPrefix: string,
): ContestacaoTemplateTopicEntry[] {
	return structure.map((topic, index) =>
		topicEntry(topic.topicId, index, topic.thesisIds, entryPrefix),
	);
}

// ── Configuração do documento ────────────────────────────────────────────────

/** Default do `processNumber` (rich text; `{{case}}` = número do processo, como no Hub) */
export const DEFAULT_PROCESS_NUMBER = '<p><strong>Processo nº {{case}}</strong></p>';

export function buildConfig(options: {
	place: string;
	signatory: AuditUser;
	/** Linha sob o nome do signatário (rich text: OAB, cargo) */
	detail: string;
}): ContestacaoTemplateConfig {
	return {
		addressing:
			'<p>EXCELENTÍSSIMO(A) SENHOR(A) DOUTOR(A) JUIZ(A) DA ___ª VARA DO TRABALHO DE ' +
			options.place.toUpperCase() +
			'</p>',
		processNumber: DEFAULT_PROCESS_NUMBER,
		presentation:
			`<p><strong>${MOCK_CONTEXT_CLIENT.name.toUpperCase()}</strong>, pessoa jurídica de direito privado, inscrita no CNPJ sob o nº ${MOCK_CONTEXT_CLIENT.cnpj}, ` +
			'por seus advogados que esta subscrevem, nos autos da Reclamação Trabalhista em epígrafe, vem, respeitosamente, à presença de Vossa Excelência, com fundamento no art. 847 da CLT, apresentar</p>',
		opening: {
			title: 'CONTESTAÇÃO',
			body: '<p>aos termos da reclamação trabalhista ajuizada, pelas razões de fato e de direito a seguir expostas.</p>',
		},
		signatures: {
			closing: '<p>Nestes termos, pede deferimento.</p>',
			place: options.place,
			date: '{{data}}',
			signatories: [{ name: options.signatory.name, detail: options.detail }],
		},
		facts: [],
		typesetting: DEFAULT_TYPESETTING,
	};
}

export const CLIENT_CONFIG = buildConfig({
	place: 'São Paulo',
	signatory: MOCK_PEOPLE.gerente,
	detail: '<p>OAB/SP 312.845 · Gerente jurídico</p>',
});
export const GLOBAL_CONFIG = buildConfig({
	place: 'São Paulo',
	signatory: MOCK_PEOPLE.admin,
	detail: '<p>OAB/SP 198.402</p>',
});

// ── Casos (revisões) ─────────────────────────────────────────────────────────

export interface MockCaseProfile {
	caseNumber: string;
	facts: Partial<Record<FactSlug, string | boolean>>;
	/** Teses que a IA sugere aprovar */
	suggested: ThesisRosterId[];
	justifications: Partial<Record<ThesisRosterId, string>>;
}

export const DEFAULT_CASE_FACTS: MockCaseProfile['facts'] = {
	'data-admissao': '2020-02-03',
	'data-demissao': '2026-04-30',
	'jornada-alegada': 'segunda a sexta-feira, das 8h às 19h, com 1 hora de intervalo',
	'possui-cartao-ponto': true,
	'motivo-rescisao': 'Dispensa sem justa causa',
	'trct-assinado': true,
};

export const DEFAULT_SUGGESTED: ThesisRosterId[] = [
	'thesis-002',
	'thesis-003',
	'thesis-006',
	'thesis-008',
	'thesis-010',
];

/** Justificativas genéricas da IA quando o caso não tem uma específica */
export const GENERIC_JUSTIFICATIONS: Record<ThesisRosterId, string> = {
	'thesis-001':
		'A inicial apresenta pedidos sem liquidação individualizada, o que atrai a exigência do art. 840, § 1º, da CLT.',
	'thesis-002':
		'O contrato ultrapassa cinco anos antes do ajuizamento; há parcelas alcançadas pela prescrição quinquenal.',
	'thesis-003':
		'Os subsídios incluem espelhos de ponto com horários variáveis e assinatura do empregado.',
	'thesis-004':
		'Há acordo individual de banco de horas nos subsídios e extratos sem saldo positivo na rescisão.',
	'thesis-005':
		'Os registros de ponto indicam término da jornada antes das 22h na maior parte do período.',
	'thesis-006':
		'O comprovante bancário demonstra o pagamento das verbas rescisórias dentro do prazo legal.',
	'thesis-007':
		'O TRCT foi assinado sem ressalvas pelo empregado, conforme documento anexo aos subsídios.',
	'thesis-008': 'A inicial não aponta testemunhas nem documentos que comprovem a ofensa alegada.',
	'thesis-009':
		'O valor pedido a título de danos morais supera os parâmetros do art. 223-G da CLT.',
	'thesis-010':
		'Pedido de honorários sucumbenciais presente na inicial; tese padrão de defesa aplicável.',
};

export const CASE_PROFILES: Record<string, MockCaseProfile> = {
	'job-ct-001': {
		caseNumber: '0010482-17.2026.5.02.0061',
		facts: {
			'data-admissao': '2019-03-11',
			'data-demissao': '2025-11-28',
			'jornada-alegada': 'segunda a sábado, das 7h às 20h, com 30 minutos de intervalo',
			'possui-cartao-ponto': true,
			'motivo-rescisao': 'Dispensa sem justa causa',
			'trct-assinado': true,
		},
		suggested: ['thesis-001', 'thesis-002', 'thesis-003', 'thesis-004', 'thesis-005', 'thesis-010'],
		justifications: {
			'thesis-001':
				'Os pedidos de reflexos das horas extras foram formulados sem valor líquido, contrariando o art. 840, § 1º, da CLT.',
			'thesis-002':
				'Admissão em 11/03/2019 e ajuizamento em 2026: parcelas anteriores a cinco anos do ajuizamento estão prescritas.',
			'thesis-003':
				'Foram juntados 80 espelhos de ponto eletrônico com marcações variáveis e assinatura mensal do reclamante.',
			'thesis-004':
				'O acordo de banco de horas de 2021 está nos subsídios e o extrato final aponta saldo zerado.',
			'thesis-005':
				'Em 94% dos dias registrados a saída ocorreu antes das 22h; nos demais, o adicional consta dos holerites.',
			'thesis-010':
				'A inicial pede honorários de 15%; a tese subsidiária de percentual mínimo é aplicável.',
		},
	},
	'job-ct-002': {
		caseNumber: '0011937-42.2026.5.02.0014',
		facts: {
			'data-admissao': '2021-06-01',
			'data-demissao': '2026-02-15',
			'jornada-alegada': '44 horas semanais, de segunda a sexta-feira',
			'possui-cartao-ponto': true,
			'motivo-rescisao': 'Rescisão indireta',
			'trct-assinado': false,
		},
		suggested: [
			'thesis-001',
			'thesis-002',
			'thesis-006',
			'thesis-007',
			'thesis-008',
			'thesis-009',
			'thesis-010',
		],
		justifications: {
			'thesis-001':
				'O pedido de rescisão indireta não indica quais condutas do art. 483 da CLT teriam sido praticadas.',
			'thesis-002':
				'Há pedidos de diferenças salariais desde 2021; a prescrição deve ser arguida por cautela.',
			'thesis-006':
				'Consta dos subsídios comprovante de depósito das verbas em 20/02/2026, dentro do prazo legal.',
			'thesis-007':
				'Tese sugerida pela modalidade de rescisão, mas o TRCT dos subsídios não está assinado pela reclamante.',
			'thesis-008':
				'A reclamante alega assédio moral sem indicar testemunhas, datas ou documentos que comprovem os fatos.',
			'thesis-009':
				'O pedido de R$ 150.000,00 a título de danos morais excede em muito os parâmetros do art. 223-G da CLT.',
			'thesis-010': 'A inicial pede honorários sucumbenciais de 15% sobre o valor da condenação.',
		},
	},
	'job-ct-003': {
		caseNumber: '0010215-88.2026.5.02.0033',
		facts: {
			'data-admissao': '2018-08-20',
			'data-demissao': '2026-05-30',
			'jornada-alegada': 'segunda a sexta-feira, das 9h às 18h',
			'possui-cartao-ponto': false,
			'motivo-rescisao': 'Pedido de demissão',
			'trct-assinado': true,
		},
		suggested: ['thesis-001', 'thesis-002', 'thesis-006', 'thesis-007', 'thesis-010'],
		justifications: {
			'thesis-001':
				'O pedido de equiparação não indica o paradigma de forma individualizada em relação a todo o período.',
			'thesis-002':
				'O contrato iniciou em 2018; as diferenças salariais anteriores a cinco anos estão prescritas.',
			'thesis-006':
				'As verbas do pedido de demissão foram pagas em 05/06/2026, conforme comprovante anexo.',
			'thesis-007': 'O TRCT foi assinado sem ressalvas pelo reclamante no pedido de demissão.',
			'thesis-010': 'Pedido de honorários de 20%, acima do limite do art. 791-A da CLT.',
		},
	},
	'job-ct-007': {
		caseNumber: '0012876-03.2026.5.02.0072',
		facts: {
			'data-admissao': '2022-01-10',
			'data-demissao': '2026-06-05',
			'jornada-alegada': 'segunda a sexta-feira, das 8h às 17h',
			'possui-cartao-ponto': true,
			'motivo-rescisao': 'Dispensa sem justa causa',
			'trct-assinado': true,
		},
		suggested: ['thesis-002', 'thesis-006', 'thesis-007', 'thesis-010'],
		justifications: {
			'thesis-002': 'Tese sugerida por padrão para contratos com pedidos de diferenças salariais.',
			'thesis-006':
				'Pagamento das verbas em 12/06/2026, sete dias após o desligamento — dentro do prazo do art. 477, § 6º.',
			'thesis-007':
				'O TRCT assinado sem ressalvas consta dos subsídios, com todas as parcelas discriminadas.',
			'thesis-010': 'A inicial pede honorários sucumbenciais; tese padrão aplicável.',
		},
	},
};

/** Estado pré-definido de cada revisão do roster (aplicado depois de montar a revisão pelo template) */
export interface MockThesisDecision {
	status: 'APPROVED' | 'REJECTED';
	reason?: ReviewRejectReason;
	description?: string;
	/** Tese adicionada manualmente (não sugerida pela IA) */
	manual?: boolean;
	/** Trecho acrescentado ao conteúdo pelo revisor */
	appendedContent?: string;
}

export interface MockReviewPreset {
	openedDaysAgo: number;
	openedBy: AuditUser;
	completedDaysAgo?: number;
	decisions: Partial<Record<ThesisRosterId, MockThesisDecision>>;
}

export const REVIEW_PRESETS: Record<string, MockReviewPreset> = {
	'job-ct-002': {
		openedDaysAgo: 1,
		openedBy: MOCK_PEOPLE.gerente,
		decisions: {
			'thesis-001': {
				status: 'APPROVED',
				appendedContent:
					'<p>Destaque-se que a Reclamante sequer indica as datas e os autores das condutas que qualifica como assédio moral, o que inviabiliza a defesa específica exigida pelo art. 341 do CPC.</p>',
			},
			'thesis-002': { status: 'APPROVED' },
			'thesis-007': { status: 'REJECTED', reason: ReviewRejectReason.TESE_NAO_APLICAVEL },
		},
	},
	'job-ct-003': {
		openedDaysAgo: 3,
		openedBy: MOCK_PEOPLE.operador,
		completedDaysAgo: 3,
		decisions: {
			'thesis-001': {
				status: 'REJECTED',
				reason: ReviewRejectReason.OUTRO,
				description:
					'A inicial indica o paradigma e liquida os pedidos; a preliminar seria rejeitada de plano.',
			},
			'thesis-002': { status: 'APPROVED' },
			'thesis-006': { status: 'APPROVED' },
			'thesis-007': { status: 'APPROVED' },
			'thesis-010': { status: 'APPROVED' },
			'thesis-008': { status: 'APPROVED', manual: true },
		},
	},
	'job-ct-007': {
		openedDaysAgo: 12,
		openedBy: MOCK_PEOPLE.gerente,
		completedDaysAgo: 12,
		decisions: {
			'thesis-002': {
				status: 'REJECTED',
				reason: ReviewRejectReason.TESE_NAO_APLICAVEL,
				description: 'Contrato de 2022 — não há parcelas alcançadas pela prescrição quinquenal.',
			},
			'thesis-006': { status: 'APPROVED' },
			'thesis-007': { status: 'APPROVED' },
			'thesis-010': { status: 'APPROVED' },
			'thesis-003': { status: 'APPROVED', manual: true },
		},
	},
};
